<script setup>
defineOptions({
  name: 'HulpvraagModal',
});

import { computed, getCurrentInstance, watch } from 'vue';
import { useForm } from '@inertiajs/vue3';
import axios from 'axios';
import Modal from '@/components/Modal.vue';
import { useToast } from '../composables/useToast.js';

// Access global properties for route function in SSR/CSR
const instance = getCurrentInstance();
const route = instance?.appContext.config.globalProperties.route;

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close']);

const { success, error } = useToast();

// Form state
const form = useForm({
  phone: '',
  subject: '',
  question: '',
  browser_agent: '',
  url_current: '',
  url_referrer: '',
  operating_system: '',
  ip: ''
});

// Character limits
const SUBJECT_MAX_LENGTH = 255;
const QUESTION_MAX_LENGTH = 510;

// Computed properties for character counts
const subjectCharsLeft = computed(() => SUBJECT_MAX_LENGTH - form.subject.length);
const questionCharsLeft = computed(() => QUESTION_MAX_LENGTH - form.question.length);

// Collect technical data when modal opens
const collectTechnicalData = () => {
  // Browser agent details
  const userAgent = navigator.userAgent;
  form.browser_agent = JSON.stringify({
    userAgent: userAgent,
    appName: navigator.appName,
    platform: navigator.platform,
    language: navigator.language
  });

  // Current URL
  form.url_current = window.location.href;

  // Referrer URL
  form.url_referrer = document.referrer;

  // Operating system detection
  form.operating_system = detectOS();
};

// Detect operating system
const detectOS = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  
  if (platform.indexOf('Win') !== -1) {
    if (userAgent.indexOf('Windows NT 10.0') !== -1) return 'Windows 10';
    if (userAgent.indexOf('Windows NT 6.3') !== -1) return 'Windows 8.1';
    if (userAgent.indexOf('Windows NT 6.2') !== -1) return 'Windows 8';
    if (userAgent.indexOf('Windows NT 6.1') !== -1) return 'Windows 7';
    return 'Windows';
  }
  if (platform.indexOf('Mac') !== -1) return 'Mac/iOS';
  if (platform.indexOf('Linux') !== -1) return 'Linux';
  if (platform.indexOf('Unix') !== -1) return 'UNIX';
  return 'Unknown';
};

// Handle modal open
const handleModalOpen = () => {
  if (props.show) {
    collectTechnicalData();
  }
};

// Submit form. The endpoint returns JSON, so this uses axios rather than an Inertia visit.
const submit = async () => {
  form.processing = true;
  form.clearErrors();

  try {
    await axios.post(route('hulpvraag.submit'), {
      phone: form.phone,
      subject: form.subject,
      question: form.question,
      browser_agent: form.browser_agent,
      url_current: form.url_current,
      url_referrer: form.url_referrer,
      operating_system: form.operating_system,
      ip: form.ip,
    });
    success('De hulpvraag is verstuurd');
    resetForm();
    emit('close');
  } catch (requestError) {
    if (requestError.response?.status === 422) {
      const fieldErrors = requestError.response.data.errors || {};
      Object.keys(fieldErrors).forEach((key) => {
        const firstError = Array.isArray(fieldErrors[key]) ? fieldErrors[key][0] : fieldErrors[key];
        form.setError(key, firstError);
      });
    }
    error('Er is een fout opgetreden bij het versturen van de hulpvraag');
    console.error('Form submission errors:', requestError);
  } finally {
    form.processing = false;
  }
};

// Reset form
const resetForm = () => {
  form.reset();
  form.clearErrors();
};

// Handle cancel
const handleCancel = () => {
  resetForm();
  emit('close');
};

watch(() => props.show, handleModalOpen, { immediate: true });
</script>

<template>
  <Modal
    :show="show"
    title="Hulpvraag WIZZR"
    size="lg"
    theme="orange"
    confirm-button-variant="orange"
    :show-default-footer="true"
    :show-cancel-button="true"
    :show-confirm-button="true"
    cancel-button-text="Annuleren"
    confirm-button-text="Versturen"
    :confirm-button-disabled="form.processing || !form.subject.trim() || !form.question.trim()"
    @close="handleCancel"
    @cancel="handleCancel"
    @confirm="submit"
    cancel-button-id="hulpvraag-cancel-button"
    confirm-button-id="hulpvraag-submit-button"
  >
    <div class="modal-form-spacing">
      <!-- Description -->
      <p class="modal-description">
        Heb je vragen over de functionaliteit van WIZZR? Wij zijn er om te helpen!
        Let op: inhoud, beleid en uitvoering vallen onder de verantwoordelijkheid van de opdrachtgever.
      </p>

      <!-- Form -->
      <form @submit.prevent="submit">
        <!-- Phone Number -->
        <div>
          <label for="hulpvraag-phone" class="form-label">
            Uw telefoonnummer
          </label>
          <input
            id="hulpvraag-phone"
            v-model="form.phone"
            type="tel"
            class="form-input"
            placeholder="Optioneel telefoonnummer"
          />
          <div v-if="form.errors.phone" class="form-error">
            {{ form.errors.phone }}
          </div>
        </div>

        <!-- Subject -->
        <div>
          <label for="hulpvraag-subject" class="form-label">
            Onderwerp <span class="form-required">*</span>
          </label>
          <input
            id="hulpvraag-subject"
            v-model="form.subject"
            type="text"
            maxlength="255"
            required
            class="form-input"
            placeholder="Korte omschrijving van uw vraag"
          />
          <div class="form-field-footer">
            <div v-if="form.errors.subject" class="form-error">
              {{ form.errors.subject }}
            </div>
            <div class="form-helper-text">
              {{ subjectCharsLeft }} karakters over
            </div>
          </div>
        </div>

        <!-- Help Question -->
        <div>
          <label for="hulpvraag-question" class="form-label">
            Hulpvraag <span class="form-required">*</span>
          </label>
          <textarea
            id="hulpvraag-question"
            v-model="form.question"
            rows="4"
            maxlength="510"
            required
            class="form-input form-textarea"
            placeholder="Beschrijf uw vraag of probleem zo gedetailleerd mogelijk..."
          ></textarea>
          <div class="form-field-footer">
            <div v-if="form.errors.question" class="form-error">
              {{ form.errors.question }}
            </div>
            <div class="form-helper-text">
              {{ questionCharsLeft }} karakters over
            </div>
          </div>
        </div>

        <!-- Hidden technical data fields -->
        <input type="hidden" id="hulpvraag-browser-agent" name="browser_agent" v-model="form.browser_agent" />
        <input type="hidden" id="hulpvraag-url-current" name="url_current" v-model="form.url_current" />
        <input type="hidden" id="hulpvraag-url-referrer" name="url_referrer" v-model="form.url_referrer" />
        <input type="hidden" id="hulpvraag-operating-system" name="operating_system" v-model="form.operating_system" />
        <input type="hidden" id="hulpvraag-ip" name="ip" v-model="form.ip" />
      </form>
    </div>
  </Modal>
</template>

